import React, { useEffect, useState } from "react";
import { useAuth } from "../../components/context/Auth";
import styles from "./ProductionAndSales.module.css";
import {
  CancelIcon2,
  NewManagerCreateIcon,
  Spinner,
} from "../../components/icons/Icon";
import Button from "../../components/updatedComponents/Dealers/Button";
import { useNavigate } from "react-router-dom";
import fetchAcTokenData from "../../APIsControll/fetchAcTokenData";
import ComponentOtpVerify from "../../components/updatedComponents/Settings/ComponentOtpVerify";
import { ModalWrapper } from "../../exports";

const ProductionAndSales = ({
  otpActoken,
  setOtpAcToken,
  managerId,
  deviceId,
  iPInfo,
  browserName,
  operatinySystem,
  show,
}) => {
  const { mode } = useAuth();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [settings, setSettings] = useState({
    productionPaymentEnabled: false,
    productionPercentage: 0,
    partialPaymentEnabled: false,
  });

  const [loading, setLoading] = useState(false);
  const [productionData, setProductionData] = useState([]);

  const OTP_LENGTH = 6;
  const [adminOtp, setAdminOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [Otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  const handleToggle = (field) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePercentageChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setSettings((prev) => ({
        ...prev,
        productionPercentage: "",
      }));
      return;
    }

    const num = Number(value);

    if (num >= 0 && num <= 100) {
      setSettings((prev) => ({
        ...prev,
        productionPercentage: num,
      }));
    }
  };

  const mapApiSettingsToState = (apiSettings) => {
    if (!apiSettings) return null;
    return {
      productionPaymentEnabled: !!apiSettings.productionPayRequired,
      productionPercentage:
        apiSettings.minRequiredPay === null ||
        apiSettings.minRequiredPay === undefined
          ? ""
          : apiSettings.minRequiredPay,
      partialPaymentEnabled: !!apiSettings.partPaymentCollection,
    };
  };

  // ─── Fetch settings (GET) ──────────────────────────────────────────────────
  const getProductionData = async () => {
    try {
      setLoading(true);

      const payload = {
        managerId,
      };

      const result = await fetchAcTokenData(
        "admin/productionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        setOtpAcToken(result?.actoken);

        const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
        if (mapped) {
          setSettings(mapped);
        }

        setProductionData(result?.data);
      } else {
        show(result?.msg || "Failed to load production & sales Data");
      }
    } catch (error) {
      console.error("production & sales error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId, iPInfo, deviceId, browserName, operatinySystem]);

  // ─── Build the common payload used for both "send otp" and "verify otp" ───
  const buildPayload = (withOtp = false) => {
    const payload = {
      managerId,
      deviceId: deviceId,
      ip: iPInfo?.ip,
      browser: browserName,
      os: operatinySystem,
      city: iPInfo?.city,
      state: iPInfo?.region,
      country: iPInfo?.country_name,
      productionPayRequired: settings.productionPaymentEnabled,
      minRequiredPay: settings.productionPercentage,
      partPaymentCollection: settings.partialPaymentEnabled,
      voucherSettingId: productionData?._id || "",
    };

    if (withOtp) {
      payload.adminEmailOtp = adminOtp.join("");
      payload.emailOtp = Otp.join("");
    }

    return payload;
  };


  const sendOtp = async () => {
    // if (!settings.productionPaymentEnabled) {
    //   show("Please enable require payment for production");
    //   return;
    // }

    const isPercentageMissing =
      settings.productionPercentage === "" ||
      settings.productionPercentage === null ||
      settings.productionPercentage === undefined ||
      settings.productionPercentage <= 0;

    if (settings.productionPaymentEnabled && isPercentageMissing) {
      show("Please pass any percentage");
      return;
    }

    try {
      setSaving(true);

      const payload = buildPayload(false);

      const result = await fetchAcTokenData(
        "admin/addUpProductionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        // backend usually returns a fresh actoken even at the "otp sent" stage
        if (result?.actoken) {
          setOtpAcToken(result?.actoken);
        }
        // reset any stale otp values before opening the modal
        setAdminOtp(Array(OTP_LENGTH).fill(""));
        setOtp(Array(OTP_LENGTH).fill(""));
        setIsOtpModalOpen(true);
      } else {
        show(result?.msg || "Failed to send OTP");
      }
    } catch (error) {
      console.error("send otp error:", error);
      show("Something went wrong while sending OTP");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseOtpModal = () => {
    setIsOtpModalOpen(false);
  };

  // ─── Step 2: Inside modal, click "Verify" -> hit the SAME API again WITH otp
  const createData = async () => {
    try {
      setVerifying(true);

      const payload = buildPayload(true);

      const result = await fetchAcTokenData(
        "admin/addUpProductionAndSales",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        // set the new actoken returned after successful otp verification
        setOtpAcToken(result?.actoken);

        // if the verify response already contains fresh settings, map + use them
        const mapped = mapApiSettingsToState(result?.data?.productionAndSales);
        if (mapped) {
          setSettings(mapped);
        }
        if (result?.data) {
          setProductionData(result?.data);
        }

        setIsOtpModalOpen(false);

        // re-fetch settings from the GET api to make sure UI reflects saved data
        await getProductionData();
      } else {
        show(result?.msg || "OTP verification failed");
      }
    } catch (error) {
      console.error("verify otp error:", error);
      show("Something went wrong while verifying OTP");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={`${styles.root} ${mode ? styles.dark : styles.light}`}>
      <ModalWrapper
        isOpen={isOtpModalOpen}
        onClose={handleCloseOtpModal}
        flag="alwaysopen"
      >
        <ComponentOtpVerify
          mode={mode}
          createData={createData}
          adminOtp={adminOtp}
          setAdminOtp={setAdminOtp}
          Otp={Otp}
          setOtp={setOtp}
          loading={verifying}
          onClose={handleCloseOtpModal}
        />
      </ModalWrapper>
      {loading ? (
        <div className={styles.loaderContainer}>
          <Spinner size="15" />
        </div>
      ) : (
        <>
          <div className={styles.nm_form}>
            <div className={styles.nm_form_section}>
              <div
                className={`${styles.settingsCard} ${
                  mode ? styles.settingsCardDark : ""
                }`}
              >
                {/* Production Payment */}

                <div className={styles.settingRow}>
                  <div className={styles.settingLeft}>
                    <div>
                      <div
                        className={`${styles.settingTitle} ${
                          mode ? styles.settingTitleDark : ""
                        }`}
                      >
                        Production Payment Requirement
                      </div>

                      <div className={styles.settingDescription}>
                        Customer must pay before production starts.
                      </div>
                    </div>

                    {settings?.productionPaymentEnabled && (
                      <div className={styles.percentInputWrapper}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={settings.productionPercentage}
                          onChange={handlePercentageChange}
                          disabled={!settings.productionPaymentEnabled}
                          className={`${styles.percentInput} ${
                            mode ? styles.percentInputDark : ""
                          }`}
                        />

                        <span className={styles.percentSymbol}>%</span>
                      </div>
                    )}
                  </div>

                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={settings.productionPaymentEnabled}
                      onChange={() => handleToggle("productionPaymentEnabled")}
                    />
                    <span className={styles.toggleSlider} />
                  </label>
                </div>

                <div className={styles.divider} />

                {/* Partial Payment */}

                <div className={styles.settingRow}>
                  <div>
                    <div
                      className={`${styles.settingTitle} ${
                        mode ? styles.settingTitleDark : ""
                      }`}
                    >
                      Partial Payment Collection
                    </div>

                    <div className={styles.settingDescription}>
                      Allow customers to pay in multiple transactions.
                    </div>
                  </div>

                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={settings.partialPaymentEnabled}
                      onChange={() => handleToggle("partialPaymentEnabled")}
                    />
                    <span className={styles.toggleSlider} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className={`${styles.nm_footer} ${mode ? styles.nm_footer_dark : ""}`}
      >
        <div className={styles.nm_footer_2}>
          <Button
            buttonIcon={<CancelIcon2 />}
            buttonText="CANCEL"
            buttonBgColor="#000"
            buttonTextColor="#fff"
            buttonBorder="none"
            buttonClick={() => navigate(-1)}
          />

          <Button
            buttonIcon={<NewManagerCreateIcon />}
            buttonText={saving ? "Sending Otp..." : "Next"}
            buttonBgColor="#4164E3"
            buttonTextColor="#fff"
            buttonBorder="none"
            disabled={saving}
            buttonClick={sendOtp}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductionAndSales;
